import { useState } from "react";
import {
    Button,
    Input,
    NonCancelableCustomEvent,
    SpaceBetween,
    TokenGroup,
    TokenGroupProps,
} from "@amzn/awsui-components-react";
import FormFieldWrapper from "./FormFieldWrapper/FormFieldWrapper";
import { CustomArrayInputProps } from "interfaces";
import { BaseKeyDetail, ClickDetail } from "@amzn/awsui-components-react/polaris/internal/events";
import { trackCustomEvent, useTrackerId } from "@amzn/uxm-aws-ui-components-react/lib/src/uxm/custom-event";

/**
 * A FormField that provides a text input
 *
 * @param {string} label - The form field label
 * @param {string} value - The current input value
 * @param {function} setValue - Function to update the current input value
 *
 *
 * @param {string} placeholder - Placeholder text for the date input
 * @param {boolean} disabled - Whether the input is disabled
 * @param {string} errorText - Error message to display
 * @param {string} warningText - Warning message to display
 * @param {string} description - Description text for the field
 * @param {React.ReactNode} info - Additional info to display
 * @param {string} [testId] - Test identifier for the component
 * @param {InputProps.Type} [type] - The type of the input
 * @param {function} onEnter - Function to be called when the user presses the enter key
 *
 * @returns {JSX.Element} A FormField component with a Select
 */
const CustomArrayInput = ({
    label,
    value,
    setValue,
    placeholder,
    disabled = false,
    errorText,
    warningText,
    description,
    info,
    testId,
    type,
    buttonText,
    touchpoint,
}: CustomArrayInputProps) => {
    const [input, setInput] = useState<string>("");

    const trackerId = useTrackerId();

    const handleOnDismiss = (event: NonCancelableCustomEvent<TokenGroupProps.DismissDetail>) => {
        const detail = event.detail;
        setValue([...value.slice(0, detail.itemIndex), ...value.slice(detail.itemIndex + 1)]);
        
        trackCustomEvent({
            action: "array-item-remove",
            itemIndex: detail.itemIndex,
        }, touchpoint, trackerId);
    };

    const handleOnKeyDown = (event: CustomEvent<BaseKeyDetail>) => {
        if (event.detail.key == "Enter") {
            event.preventDefault();
            addOptionToArray();
        }
    };

    const handleOnSubmit = (event: CustomEvent<ClickDetail>) => {
        event.preventDefault();
        addOptionToArray();
    };

    const addOptionToArray = () => {
        setValue([...value, input]);
        setInput("");
        
        trackCustomEvent({
            action: "array-item-add",
            value: input,
        }, touchpoint, trackerId);
    };

    const selectedInput = (
        <>
            <SpaceBetween size="m">
                <SpaceBetween size="xs">
                    <Input
                        type={type}
                        onChange={({ detail }) => setInput(detail.value)}
                        value={input}
                        placeholder={placeholder}
                        disabled={disabled}
                        onKeyDown={handleOnKeyDown}
                    />
                    <Button onClick={handleOnSubmit}>{buttonText ? buttonText : "Add"}</Button>
                </SpaceBetween>
                <TokenGroup
                    data-testid="watcher-list"
                    onDismiss={handleOnDismiss}
                    items={value.map((option) => ({
                        label: option,
                        dismissLabel: `Remove option ${option}`,
                    }))}
                />
            </SpaceBetween>
        </>
    );

    return (
        <FormFieldWrapper
            testId={testId}
            label={label}
            errorText={errorText}
            warningText={warningText}
            description={description}
            info={info}
            input={selectedInput}
        />
    );
};

export default CustomArrayInput;
